import { 
  createSlice, 
  createSelector, 
  PayloadAction 
} from '@reduxjs/toolkit';
import { RootState } from '../index';
import { 
  AppUIState,
  ToastType,
  ModalSize,
  AlertType,
  ConfirmDialogType
} from '../../types/ui';

// Initial state
const initialState: AppUIState = {
  // Collections
  toasts: {},
  modals: {},
  alerts: {},
  
  // Active states
  activeConfirmDialog: null,
  
  // Navigation/menu state
  menu: {
    isMainMenuOpen: false,
    isNotificationsOpen: false,
    isSearchOpen: false,
    isSidebarOpen: false,
    activeTab: 'home'
  },
  
  // Layout state
  layout: {
    isHeaderVisible: true,
    isFooterVisible: true,
    isNavbarVisible: true,
    contentWidth: 'constrained',
    contentPadding: 'medium',
    scrollPosition: 0
  },
  
  // Interaction tracking
  interaction: {
    clickedElement: null,
    hoveredElement: null,
    focusedElement: null,
    lastInteractionTime: null,
    isUserInteracting: false,
    isInitialLoadComplete: false
  },
  
  // Loading states
  isGlobalLoading: false,
  pageLoadingStatus: {},
  
  // Callbacks registry
  callbackRegistry: {}
};

// Helper function to generate unique IDs
const generateId = (prefix: string): string => {
  return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
};

// Create the UI slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Toast actions
    showToast: (state, action: PayloadAction<{
      type: ToastType;
      message: string;
      title?: string;
      autoClose?: boolean;
      duration?: number;
      actionLabel?: string;
      actionCallback?: string;
    }>) => {
      const id = generateId('toast');
      state.toasts[id] = {
        id,
        ...action.payload,
        autoClose: action.payload.autoClose ?? true,
        duration: action.payload.duration ?? 5000,
        createdAt: Date.now()
      };
    },
    
    dismissToast: (state, action: PayloadAction<string>) => {
      const { [action.payload]: removed, ...remaining } = state.toasts;
      state.toasts = remaining;
    },
    
    clearAllToasts: (state) => {
      state.toasts = {};
    },
    
    // Modal actions
    showModal: (state, action: PayloadAction<{
      component: string;
      props?: Record<string, any>;
      title?: string;
      size?: ModalSize;
      closable?: boolean;
      fullscreen?: boolean;
      hasBackdrop?: boolean;
      backdropClickClose?: boolean;
      onClose?: string;
    }>) => {
      const id = generateId('modal');
      state.modals[id] = {
        id,
        ...action.payload,
        closable: action.payload.closable ?? true,
        hasBackdrop: action.payload.hasBackdrop ?? true,
        backdropClickClose: action.payload.backdropClickClose ?? true,
        size: action.payload.size ?? 'medium',
        createdAt: Date.now()
      };
    },
    
    closeModal: (state, action: PayloadAction<string>) => {
      const { [action.payload]: removed, ...remaining } = state.modals;
      state.modals = remaining;
    },
    
    closeAllModals: (state) => {
      state.modals = {};
    },
    
    // Alert actions
    showAlert: (state, action: PayloadAction<{
      type: AlertType;
      message: string;
      title?: string;
      dismissable?: boolean;
      expiresAt?: number;
    }>) => {
      const id = generateId('alert');
      state.alerts[id] = {
        id,
        ...action.payload,
        dismissable: action.payload.dismissable ?? true,
        createdAt: Date.now()
      };
    },
    
    dismissAlert: (state, action: PayloadAction<string>) => {
      const { [action.payload]: removed, ...remaining } = state.alerts;
      state.alerts = remaining;
    },
    
    clearAllAlerts: (state) => {
      state.alerts = {};
    },
    
    // Confirm dialog actions
    showConfirmDialog: (state, action: PayloadAction<{
      type: ConfirmDialogType;
      message: string;
      title?: string;
      confirmLabel?: string;
      cancelLabel?: string;
      confirmCallback: string;
      cancelCallback?: string;
      destructive?: boolean;
    }>) => {
      state.activeConfirmDialog = {
        id: generateId('confirm'),
        ...action.payload,
        destructive: action.payload.destructive ?? false
      };
    },
    
    dismissConfirmDialog: (state) => {
      state.activeConfirmDialog = null;
    },
    
    // Menu actions
    toggleMainMenu: (state, action: PayloadAction<boolean | undefined>) => {
      state.menu.isMainMenuOpen = action.payload !== undefined 
        ? action.payload 
        : !state.menu.isMainMenuOpen;
        
      // Close other menus if opening main menu
      if (state.menu.isMainMenuOpen) {
        state.menu.isNotificationsOpen = false;
        state.menu.isSearchOpen = false;
      }
    },
    
    toggleNotifications: (state, action: PayloadAction<boolean | undefined>) => {
      state.menu.isNotificationsOpen = action.payload !== undefined 
        ? action.payload 
        : !state.menu.isNotificationsOpen;
        
      // Close other menus if opening notifications
      if (state.menu.isNotificationsOpen) {
        state.menu.isMainMenuOpen = false;
        state.menu.isSearchOpen = false;
      }
    },
    
    toggleSearch: (state, action: PayloadAction<boolean | undefined>) => {
      state.menu.isSearchOpen = action.payload !== undefined 
        ? action.payload 
        : !state.menu.isSearchOpen;
        
      // Close other menus if opening search
      if (state.menu.isSearchOpen) {
        state.menu.isMainMenuOpen = false;
        state.menu.isNotificationsOpen = false;
      }
    },
    
    toggleSidebar: (state, action: PayloadAction<boolean | undefined>) => {
      state.menu.isSidebarOpen = action.payload !== undefined 
        ? action.payload 
        : !state.menu.isSidebarOpen;
    },
    
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.menu.activeTab = action.payload;
    },
    
    // Layout actions
    setHeaderVisibility: (state, action: PayloadAction<boolean>) => {
      state.layout.isHeaderVisible = action.payload;
    },
    
    setFooterVisibility: (state, action: PayloadAction<boolean>) => {
      state.layout.isFooterVisible = action.payload;
    },
    
    setNavbarVisibility: (state, action: PayloadAction<boolean>) => {
      state.layout.isNavbarVisible = action.payload;
    },
    
    setContentWidth: (state, action: PayloadAction<'full' | 'constrained'>) => {
      state.layout.contentWidth = action.payload;
    },
    
    setContentPadding: (state, action: PayloadAction<'none' | 'small' | 'medium' | 'large'>) => {
      state.layout.contentPadding = action.payload;
    },
    
    setScrollPosition: (state, action: PayloadAction<number>) => {
      state.layout.scrollPosition = action.payload;
    },
    
    // Interaction tracking
    trackClick: (state, action: PayloadAction<string | null>) => {
      state.interaction.clickedElement = action.payload;
      state.interaction.lastInteractionTime = Date.now();
      state.interaction.isUserInteracting = true;
    },
    
    trackHover: (state, action: PayloadAction<string | null>) => {
      state.interaction.hoveredElement = action.payload;
      state.interaction.lastInteractionTime = Date.now();
      state.interaction.isUserInteracting = true;
    },
    
    trackFocus: (state, action: PayloadAction<string | null>) => {
      state.interaction.focusedElement = action.payload;
      state.interaction.lastInteractionTime = Date.now();
      state.interaction.isUserInteracting = true;
    },
    
    setInitialLoadComplete: (state, action: PayloadAction<boolean>) => {
      state.interaction.isInitialLoadComplete = action.payload;
    },
    
    // Loading states
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.isGlobalLoading = action.payload;
    },
    
    setPageLoading: (state, action: PayloadAction<{page: string, isLoading: boolean}>) => {
      state.pageLoadingStatus[action.payload.page] = action.payload.isLoading;
    },
    
    // Callback registry
    registerCallback: (state, action: PayloadAction<{id: string, description: string}>) => {
      state.callbackRegistry[action.payload.id] = action.payload.description;
    },
    
    unregisterCallback: (state, action: PayloadAction<string>) => {
      const { [action.payload]: removed, ...remaining } = state.callbackRegistry;
      state.callbackRegistry = remaining;
    },
    
    clearCallbackRegistry: (state) => {
      state.callbackRegistry = {};
    },
    
    // Reset UI state
    resetUIState: (state) => {
      return {
        ...initialState,
        // Preserve certain states if needed
        interaction: {
          ...initialState.interaction,
          isInitialLoadComplete: state.interaction.isInitialLoadComplete
        }
      };
    }
  }
});

// Export actions
export const {
  // Toast actions
  showToast,
  dismissToast,
  clearAllToasts,
  
  // Modal actions
  showModal,
  closeModal,
  closeAllModals,
  
  // Alert actions
  showAlert,
  dismissAlert,
  clearAllAlerts,
  
  // Confirm dialog actions
  showConfirmDialog,
  dismissConfirmDialog,
  
  // Menu actions
  toggleMainMenu,
  toggleNotifications,
  toggleSearch,
  toggleSidebar,
  setActiveTab,
  
  // Layout actions
  setHeaderVisibility,
  setFooterVisibility,
  setNavbarVisibility,
  setContentWidth,
  setContentPadding,
  setScrollPosition,
  
  // Interaction tracking
  trackClick,
  trackHover,
  trackFocus,
  setInitialLoadComplete,
  
  // Loading states
  setGlobalLoading,
  setPageLoading,
  
  // Callback registry
  registerCallback,
  unregisterCallback,
  clearCallbackRegistry,
  
  // Reset UI state
  resetUIState
} = uiSlice.actions;

// Basic selectors
export const selectUI = (state: RootState) => state.ui;

export const selectToasts = createSelector(
  selectUI,
  (ui) => Object.values(ui.toasts)
);

export const selectModals = createSelector(
  selectUI,
  (ui) => Object.values(ui.modals)
);

export const selectAlerts = createSelector(
  selectUI,
  (ui) => Object.values(ui.alerts)
);

export const selectActiveConfirmDialog = createSelector(
  selectUI,
  (ui) => ui.activeConfirmDialog
);

export const selectMenu = createSelector(
  selectUI,
  (ui) => ui.menu
);

export const selectLayout = createSelector(
  selectUI,
  (ui) => ui.layout
);

export const selectInteraction = createSelector(
  selectUI,
  (ui) => ui.interaction
);

export const selectIsGlobalLoading = createSelector(
  selectUI,
  (ui) => ui.isGlobalLoading
);

export const selectPageLoadingStatus = createSelector(
  selectUI,
  (ui) => ui.pageLoadingStatus
);

// Specific UI state selectors
export const selectIsMainMenuOpen = createSelector(
  selectMenu,
  (menu) => menu.isMainMenuOpen
);

export const selectIsNotificationsOpen = createSelector(
  selectMenu,
  (menu) => menu.isNotificationsOpen
);

export const selectIsSearchOpen = createSelector(
  selectMenu,
  (menu) => menu.isSearchOpen
);

export const selectIsSidebarOpen = createSelector(
  selectMenu,
  (menu) => menu.isSidebarOpen
);

export const selectActiveTab = createSelector(
  selectMenu,
  (menu) => menu.activeTab
);

export const selectIsAnyMenuOpen = createSelector(
  selectMenu,
  (menu) => menu.isMainMenuOpen || menu.isNotificationsOpen || menu.isSearchOpen
);

export const selectIsHeaderVisible = createSelector(
  selectLayout,
  (layout) => layout.isHeaderVisible
);

export const selectIsFooterVisible = createSelector(
  selectLayout,
  (layout) => layout.isFooterVisible
);

export const selectIsNavbarVisible = createSelector(
  selectLayout,
  (layout) => layout.isNavbarVisible
);

export const selectScrollPosition = createSelector(
  selectLayout,
  (layout) => layout.scrollPosition
);

export const selectContentWidth = createSelector(
  selectLayout,
  (layout) => layout.contentWidth
);

export const selectContentPadding = createSelector(
  selectLayout,
  (layout) => layout.contentPadding
);

export const selectLastInteractionTime = createSelector(
  selectInteraction,
  (interaction) => interaction.lastInteractionTime
);

export const selectIsUserInteracting = createSelector(
  selectInteraction,
  (interaction) => interaction.isUserInteracting
);

export const selectIsInitialLoadComplete = createSelector(
  selectInteraction,
  (interaction) => interaction.isInitialLoadComplete
);

export const selectPageIsLoading = (page: string) => createSelector(
  selectPageLoadingStatus,
  (pageLoadingStatus) => !!pageLoadingStatus[page]
);

export const selectIsAnyModalOpen = createSelector(
  selectModals,
  (modals) => modals.length > 0
);

export const selectHasActiveConfirmDialog = createSelector(
  selectActiveConfirmDialog,
  (dialog) => dialog !== null
);

export default uiSlice.reducer;